import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, ArrowUpRight, Check, Stamp, Languages, Home as HomeIcon, Landmark, HeartPulse, GraduationCap, Quote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT, IMAGES } from "@/i18n/translations";
import MaskedReveal from "@/components/MaskedReveal";
import Reveal from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import LeadQuiz from "@/components/LeadQuiz";
import AzulejoStrip from "@/components/AzulejoStrip";
import Seo from "@/components/Seo";

const catIcons = [Stamp, Languages, HomeIcon, Landmark, HeartPulse, GraduationCap];
const catColors = ["text-azul", "text-citrus", "text-bronze"];

const Chapter = ({ num, overline, title }) => (
  <div className="mb-12 md:mb-16">
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">
      {num} — {overline}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.15] text-ink max-w-2xl">{title}</h2>
  </div>
);

const Home = () => {
  const { t } = useLanguage();
  const heroImgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroImgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Marina Knows — NIE, TIE & Paperwork Help in Valencia | Comunidad Valenciana"
        description="15 years helping foreigners navigate Spanish bureaucracy in the Comunidad Valenciana: NIE Valencia, TIE appointments, empadronamiento, translation and residency support."
      />

      {/* HERO — full-bleed Valencia panorama */}
      <section ref={heroImgRef} className="relative overflow-hidden" data-testid="hero">
        <motion.img
          src={IMAGES.pano}
          alt="Sunlit street in Valencia's old town with orange trees"
          className="absolute inset-0 w-full h-[115%] object-cover"
          style={{ y: imgY }}
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sand via-sand/85 to-sand/15" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-sand to-transparent" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-24 md:pb-36">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-6"
              data-testid="hero-overline"
            >
              {t.hero.overline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
              data-testid="hero-cameo"
            >
              <img
                src="/images/marina-cameo.jpg"
                alt="Marina, founder of Marina Knows"
                className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-terracotta"
              />
              <span className="font-serif italic text-lg text-bronze">Marina · Valencia</span>
            </motion.div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.08] text-ink" data-testid="hero-heading">
              <MaskedReveal lines={t.hero.lines} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8 text-lg md:text-xl leading-relaxed text-ink/80 max-w-xl"
              data-testid="hero-sub"
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              >
                <MessageCircle size={18} aria-hidden />
                {t.hero.ctaPrimary}
              </a>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-2 border border-ink/25 bg-white/50 backdrop-blur-sm hover:border-terracotta hover:text-bronze text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              >
                {t.hero.ctaSecondary}
                <ArrowRight size={18} aria-hidden />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-8 inline-flex bg-white/85 backdrop-blur-sm border border-stone rounded-full px-5 py-3 font-mono text-xs text-ink/80"
              data-testid="hero-badge"
            >
              {t.hero.badge}
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* LEAD QUIZ */}
      <section className="bg-cream border-b border-stone" data-testid="quiz-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
          <LeadQuiz />
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="services-overview">
        <Reveal>
          <Chapter num={t.home.ch1} overline={t.home.servicesOverline} title={t.home.servicesTitle} />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg text-ink/70 max-w-2xl -mt-6 mb-14">{t.home.servicesSub}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {t.home.categories.map((cat, i) => {
            const Icon = catIcons[i];
            const span = i % 3 === 0 ? "md:col-span-5" : i % 3 === 1 ? "md:col-span-4" : "md:col-span-3";
            return (
              <Reveal key={i} delay={i * 0.06} className={span}>
                <Link
                  to="/services"
                  data-testid={`service-cat-${i}`}
                  className="group block bg-white border border-stone rounded-2xl p-8 h-full transition-all hover:border-terracotta/50 hover:-translate-y-1"
                >
                  <Icon size={26} className={`${catColors[i % 3]} mb-5`} aria-hidden />
                  <h3 className="font-serif text-2xl text-ink mb-3">{cat.title}</h3>
                  <p className="text-ink/65 leading-relaxed mb-6">{cat.desc}</p>
                  <p className="flex items-center justify-between font-mono text-sm text-olive">
                    {cat.price}
                    <ArrowUpRight size={18} className="text-stone-fg group-hover:text-bronze transition-colors" aria-hidden />
                  </p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <AzulejoStrip />

      {/* WHY MARINA */}
      <section className="bg-cream border-y border-stone" data-testid="why-marina">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Chapter num={t.home.ch2} overline={t.home.whyOverline} title={t.home.whyTitle} />
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/about" data-testid="why-about-cta" className="inline-flex items-center gap-2 text-bronze font-medium hover:gap-3 transition-all">
                {t.home.whyCta} <ArrowRight size={18} aria-hidden />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg leading-relaxed text-ink/80 mb-5">{t.home.whyBody1}</p>
              <p className="text-lg leading-relaxed text-ink/80 mb-10">{t.home.whyBody2}</p>
            </Reveal>
            <ul className="space-y-4">
              {t.home.whyPoints.map((point, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <li className="flex items-start gap-3 text-ink/80" data-testid={`why-point-${i}`}>
                    <Check size={20} className="text-olive mt-0.5 shrink-0" aria-hidden />
                    {point}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="packages-home">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{t.home.packagesOverline}</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink mb-14">{t.home.packagesTitle}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7">
            <div className="bg-terracotta text-ink rounded-2xl p-8 md:p-12 h-full flex flex-col" data-testid="pkg-first-90">
              <h3 className="font-serif text-3xl md:text-4xl mb-4">{t.home.pkg1Name}</h3>
              <p className="text-ink/85 leading-relaxed mb-8 max-w-md">{t.home.pkg1Desc}</p>
              <div className="mt-auto flex flex-wrap items-end gap-x-4 gap-y-1">
                <span className="font-serif text-4xl md:text-5xl">{t.home.pkg1Price}</span>
                <span className="font-mono text-xs text-ink/70 pb-1.5">{t.home.pkg1Note}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="bg-white border border-stone rounded-2xl p-8 md:p-12 h-full flex flex-col" data-testid="pkg-settling-in">
              <h3 className="font-serif text-3xl text-ink mb-4">{t.home.pkg2Name}</h3>
              <p className="text-ink/70 leading-relaxed mb-8">{t.home.pkg2Desc}</p>
              <div className="mt-auto flex flex-wrap items-end gap-x-4 gap-y-1">
                <span className="font-serif text-4xl md:text-5xl text-ink break-words">{t.home.pkg2Price}</span>
                <span className="font-mono text-xs text-stone-fg pb-1.5">{t.home.pkg2Note}</span>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <Link to="/pricing" data-testid="packages-cta" className="mt-10 inline-flex items-center gap-2 border border-ink/20 hover:border-terracotta hover:text-bronze text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
            {t.home.pkgCta} <ArrowRight size={18} aria-hidden />
          </Link>
        </Reveal>
      </section>

      {/* TESTIMONIALS (placeholder) */}
      <section className="bg-cream border-y border-stone" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{t.home.testimonialsOverline}</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink mb-4">{t.home.testimonialsTitle}</h2>
            <p className="text-sm text-stone-fg font-mono mb-14" data-testid="testimonials-placeholder-note">{t.home.testimonialsNote}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.home.testimonials.map((tst, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="bg-white border border-stone rounded-2xl p-8 h-full" data-testid={`testimonial-${i}`}>
                  <Quote size={22} className="text-citrus mb-5" aria-hidden />
                  <blockquote className="font-serif italic text-xl leading-relaxed text-ink mb-6">“{tst.quote}”</blockquote>
                  <figcaption>
                    <p className="font-medium text-ink">{tst.name}</p>
                    <p className="text-sm text-stone-fg">{tst.detail}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AzulejoStrip />

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="service-area">
        <div className="lg:col-span-6">
          <Reveal>
            <Chapter num={t.home.ch3} overline={t.home.areaOverline} title={t.home.areaTitle} />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-lg text-ink/70 mb-8 -mt-6">{t.home.areaSub}</p>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {t.home.areas.map((area, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <span data-testid={`area-chip-${i}`} className="inline-block border border-azul/40 text-azul-dark font-medium text-sm px-4 py-2 rounded-full">
                  {area}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="lg:col-span-6" delay={0.1}>
          <div className="overflow-hidden rounded-2xl border border-stone">
            <img src={IMAGES.area} alt="Mediterranean street in the Comunidad Valenciana" className="w-full h-[340px] md:h-[440px] object-cover" data-testid="area-image" />
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="bg-ink text-sand" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-6">{t.home.ctaTitle}</h2>
            <p className="text-lg text-sand/70 max-w-xl mx-auto mb-10">{t.home.ctaSub}</p>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={18} aria-hidden />
              {t.home.ctaButton}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
